/**
 * SpeedTempoSection — B-2026-0114 (Phase 0 · P0-4)
 *
 * Renders per-phase Speed & Tempo results from `metadata.speed_assessment`.
 * Core rule: COLOR = VERDICT (not raw direction). Redundant encoding:
 * color + icon + word-label + direction arrow — never color alone.
 *
 * Mounts inside a <CollapsibleSection> in EvaluationResults.tsx.
 * Renders nothing when all phases are `not_evaluated`.
 */

import { cn } from '@/utils/cn';
import type { SpeedAssessment, SpeedVerdict, SpeedMagnitude, SpeedDirection } from '@/types/api';

// ─── Props ──────────────────────────────────────────────────────────────────

export interface SpeedTempoSectionProps {
  speedAssessment: SpeedAssessment[];
}

// ─── Color palette — keyed by verdict/magnitude ──────────────────────────────

interface VerdictColors {
  text: string;
  bg: string;
  border: string;
  marker: string;
}

function getVerdictColors(
  verdict: SpeedVerdict,
  magnitude: SpeedMagnitude | null,
): VerdictColors | null {
  if (verdict === 'not_evaluated') return null;
  if (verdict === 'on_target') {
    return {
      text: 'text-emerald-400',
      bg: 'bg-emerald-400/10',
      border: 'border-emerald-400/20',
      marker: 'bg-emerald-400',
    };
  }
  // too_fast | too_slow
  if (magnitude === 'slight') {
    return {
      text: 'text-text-muted',
      bg: 'bg-white/5',
      border: 'border-white/10',
      marker: 'bg-white/40',
    };
  }
  if (magnitude === 'notable') {
    return {
      text: 'text-amber-400',
      bg: 'bg-amber-400/10',
      border: 'border-amber-400/20',
      marker: 'bg-amber-400',
    };
  }
  // significant
  return {
    text: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/20',
    marker: 'bg-red-400',
  };
}

// ─── Label/icon helpers ───────────────────────────────────────────────────────

/**
 * Primary status icon.
 * ⚡ / ✓ for on_target; ≈ for slight; ▲/▼ for notable/significant.
 * ▲ = faster (negative delta_ms); ▼ = slower (positive delta_ms).
 */
function getStatusIcon(
  verdict: SpeedVerdict,
  direction: SpeedDirection,
  magnitude: SpeedMagnitude | null,
): string {
  if (verdict === 'on_target') {
    return direction === 'reward_faster' ? '⚡' : '✓';
  }
  if (magnitude === 'slight') return '≈';
  return verdict === 'too_fast' ? '▲' : '▼';
}

/**
 * Extra direction arrow shown alongside the icon — only for on_target
 * when a meaningful delta is present, so directional intent is still
 * communicated without relying on color alone (deuteranopia guard).
 */
function getDirectionArrow(verdict: SpeedVerdict, deltaMs: number | null): string {
  if (verdict !== 'on_target' || deltaMs === null || deltaMs === 0) return '';
  return deltaMs < 0 ? '▲' : '▼';
}

function getVerdictLabel(
  verdict: SpeedVerdict,
  direction: SpeedDirection,
  magnitude: SpeedMagnitude | null,
): string {
  if (verdict === 'on_target') {
    if (direction === 'reward_faster') return 'Explosive';
    if (direction === 'reward_slower') return 'Controlled';
    return 'On time';
  }
  if (verdict === 'too_fast') {
    if (magnitude === 'slight') return 'Close';
    return magnitude === 'notable' ? 'A bit fast' : 'Too fast';
  }
  if (verdict === 'too_slow') {
    if (magnitude === 'slight') return 'Close';
    return magnitude === 'notable' ? 'A bit slow' : 'Too slow';
  }
  return '';
}

/** "Coach wants" chip label. Maps the canonical direction to athlete-friendly copy. */
function getDirectionChipLabel(direction: SpeedDirection): string {
  if (direction === 'match') return 'Matched speed';
  if (direction === 'reward_faster') return 'Faster';
  if (direction === 'reward_slower') return 'Slower';
  return '';
}

// ─── Duration formatting ─────────────────────────────────────────────────────

function formatSec(ms: number | null): string {
  if (ms === null) return '—';
  const s = Math.abs(ms) / 1000;
  // Remove trailing zeros but keep at least one decimal for values < 10s
  if (s < 10) return `${s.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}s`;
  return `${s.toFixed(1)}s`;
}

// ─── Coaching tip ────────────────────────────────────────────────────────────

function getFixCopy(
  verdict: SpeedVerdict,
  magnitude: SpeedMagnitude | null,
  direction: SpeedDirection,
  deltaMs: number | null,
  deltaPct: number | null,
): string | null {
  if (verdict !== 'too_fast' && verdict !== 'too_slow') return null;
  if (magnitude === 'slight') return null; // inside band — no nag

  const msAbs = deltaMs !== null ? `${(Math.abs(deltaMs) / 1000).toFixed(1)}s` : null;
  const pctAbs = deltaPct !== null ? `${Math.abs(Math.round(deltaPct))}%` : null;
  const amount = msAbs ?? pctAbs ?? 'some time';

  if (verdict === 'too_fast') {
    if (direction === 'match') return `Slow down — try spending about ${amount} more in this phase.`;
    if (direction === 'reward_slower') return `This phase rewards slower execution — aim for about ${amount} more.`;
    return `Pace was above the target range.`;
  }
  // too_slow
  if (direction === 'match') return `Speed up — aim to complete this phase about ${amount} faster.`;
  if (direction === 'reward_faster') return `This phase rewards faster execution — try to be about ${amount} quicker.`;
  return `Pace was below the target range.`;
}

// ─── Tolerance bar position ──────────────────────────────────────────────────

/**
 * Maps delta_pct to a 0–100 position on the tolerance bar.
 * delta_pct < 0 = faster = left of center; > 0 = slower = right.
 * Clamped to [2, 98] so the marker stays within the track.
 */
function getMarkerPos(deltaPct: number | null): number {
  if (deltaPct === null) return 50;
  return Math.max(2, Math.min(98, 50 + deltaPct));
}

// ─── Per-phase card ──────────────────────────────────────────────────────────

function PhaseSpeedCard({ phase }: { phase: SpeedAssessment }) {
  const {
    verdict, magnitude, direction,
    delta_ms, delta_pct,
    template_duration_ms, athlete_duration_ms,
    phase_label,
  } = phase;

  const colors = getVerdictColors(verdict, magnitude);
  if (!colors) return null; // not_evaluated — omit silently

  const statusIcon   = getStatusIcon(verdict, direction, magnitude);
  const dirArrow     = getDirectionArrow(verdict, delta_ms);
  const label        = getVerdictLabel(verdict, direction, magnitude);
  const dirChip      = getDirectionChipLabel(direction);
  const markerPos    = getMarkerPos(delta_pct);
  const fix          = getFixCopy(verdict, magnitude, direction, delta_ms, delta_pct);

  // Sign prefix for delta display: "−0.3s" (en-dash) or "+0.3s"
  const deltaSign    = delta_ms !== null ? (delta_ms < 0 ? '−' : '+') : '';
  const deltaSec     = delta_ms !== null ? formatSec(Math.abs(delta_ms)) : null;
  const pctDisplay   = delta_pct !== null ? `${Math.abs(Math.round(delta_pct))}%` : null;

  // aria-label summarises verdict direction for screen readers
  const ariaDir = delta_ms === null ? '' : delta_ms < 0 ? ', faster than target' : delta_ms > 0 ? ', slower than target' : '';
  const badgeAriaLabel = `Speed verdict: ${label}${ariaDir}`;

  return (
    <div
      className={cn('rounded-lg border p-3 space-y-3', colors.bg, colors.border)}
      data-testid={`speed-phase-${phase_label}`}
    >
      {/* ── Header row: phase label + verdict badge ─────────────────────── */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary capitalize">{phase_label}</span>

        {/* Badge — redundant encoding: icon + word + optional direction arrow */}
        <span
          className={cn('inline-flex items-center gap-1 text-xs font-semibold', colors.text)}
          aria-label={badgeAriaLabel}
        >
          <span aria-hidden="true">{statusIcon}</span>
          <span>{label}</span>
          {dirArrow && <span aria-hidden="true">{dirArrow}</span>}
        </span>
      </div>

      {/* ── Timing headline: "0.7s vs 1.0s · −0.3s / 30%" ─────────────── */}
      <div className="text-xs font-mono">
        <span className="text-text-secondary">{formatSec(athlete_duration_ms)}</span>
        <span className="text-text-subtle mx-1">vs</span>
        <span className="text-text-muted">{formatSec(template_duration_ms)}</span>
        {deltaSec !== null && pctDisplay !== null && (
          <>
            <span className="text-text-subtle mx-1">·</span>
            <span className={colors.text}>
              {deltaSign}{deltaSec} / {pctDisplay}
            </span>
          </>
        )}
      </div>

      {/* ── Tolerance bar: left = faster, center = on-target, right = slower */}
      <div className="space-y-1" aria-hidden="true">
        <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
          {/* Tolerance band — center ±10% */}
          <div
            className="absolute top-0 h-full bg-emerald-400/20 rounded-full"
            style={{ left: '40%', width: '20%' }}
          />
          {/* Athlete marker */}
          <div
            className={cn('absolute top-0 h-full w-1 -translate-x-1/2', colors.marker)}
            style={{ left: `${markerPos}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-text-muted select-none">
          <span>▲ Faster</span>
          <span>Target</span>
          <span>Slower ▼</span>
        </div>
      </div>

      {/* ── Coach wants chip ─────────────────────────────────────────────── */}
      {dirChip && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-text-muted">Coach wants:</span>
          <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded bg-blue-400/10 text-blue-400 border border-blue-400/20">
            {dirChip}
          </span>
        </div>
      )}

      {/* ── One-line coaching tip (suppressed for on_target and slight) ──── */}
      {fix && <p className="text-xs text-text-muted">{fix}</p>}
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────────────────

export default function SpeedTempoSection({ speedAssessment }: SpeedTempoSectionProps) {
  // Omit not_evaluated phases — they contribute nothing to display
  const evaluatedPhases = speedAssessment.filter(p => p.verdict !== 'not_evaluated');

  if (evaluatedPhases.length === 0) return null;

  return (
    <div className="space-y-3">
      {evaluatedPhases.map((phase, i) => (
        <PhaseSpeedCard key={phase.phase_label ?? i} phase={phase} />
      ))}
    </div>
  );
}
